<script setup lang="ts">
/**
 * Always-available quick reference (the toolbar "?" button). Mirrors the user
 * guide in miniature: the core idea, getting around, modes, tools, and Pro.
 * Offers to replay the welcome tour.
 */
import { IconX, IconSparkles } from '@tabler/icons-vue';
import AppLogo from '@/components/AppLogo.vue';
import { useUiStore } from '@/stores/ui';

const ui = useUiStore();

function close(): void {
  ui.showHelp = false;
}
</script>

<template>
  <div v-if="ui.showHelp" class="sheet-backdrop" @click.self="close">
    <div class="sheet" role="dialog" aria-modal="true" aria-label="Help">
      <header>
        <div class="title">
          <AppLogo :size="20" />
          <h2>Help</h2>
        </div>
        <button class="icon-close" aria-label="Close" @click="close">
          <IconX :size="18" stroke-width="2" />
        </button>
      </header>

      <p class="lead">
        <strong>Draw once, draw everywhere.</strong> Your line repeats across every
        tile and wraps seamlessly across the edges — that's the whole idea.
      </p>

      <section>
        <h3>Getting around</h3>
        <ul>
          <li><b>Draw</b> — one finger, or click-drag with the mouse.</li>
          <li><b>Pan</b> — two-finger drag, or Shift-drag / two-finger scroll.</li>
          <li><b>Zoom</b> — pinch, or Ctrl/⌘ + scroll.</li>
        </ul>
      </section>

      <section>
        <h3>Modes</h3>
        <ul>
          <li><b>Draw</b> — textured coloured pencil; pressure and tilt change its weight.</li>
          <li><b>Trace (Pro)</b> — bezier pen: click to add points, drag for curves, click the first point to close, Enter to finish, Esc to cancel.</li>
        </ul>
      </section>

      <section>
        <h3>Tools</h3>
        <ul>
          <li><b>Draw</b> &amp; <b>Erase</b> — both wrap across tile edges.</li>
          <li><b>Tessellation panel</b> — toggle the tessellated ghost copies, rotate the view, and choose how colours tile across the pattern.</li>
          <li><b>Layers</b> — up to five; fade a rough layer and refine on a fresh one.</li>
        </ul>
      </section>

      <section>
        <h3>Patterns &amp; saving</h3>
        <ul>
          <li>Pick a <b>shape</b> and a <b>symmetry</b> in the Pattern panel — the drawing re-tiles instantly.</li>
          <li>Everything <b>autosaves</b> to your device. Open <b>Sketches</b> to manage them.</li>
        </ul>
      </section>

      <button class="replay-btn" @click="ui.replayOnboarding()">
        <IconSparkles :size="15" stroke-width="1.75" />
        Replay the welcome tour
      </button>
    </div>
  </div>
</template>

<style scoped>
.sheet-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 8, 16, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: max(16px, var(--safe-top)) 16px max(16px, var(--safe-bottom));
}

.sheet {
  width: min(440px, 100%);
  max-height: 100%;
  overflow-y: auto;
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  border-radius: 12px;
  padding: 20px 22px;
}

header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.title h2 {
  margin: 0;
  font-size: 18px;
  color: var(--text-primary);
}

.icon-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--text-muted);
  cursor: pointer;
}

.icon-close:hover {
  color: var(--text-primary);
}

.lead {
  margin: 0 0 16px;
  font-size: 13.5px;
  line-height: 1.6;
  color: var(--text-muted);
}

section {
  margin-bottom: 14px;
}

h3 {
  margin: 0 0 6px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--text-muted);
}

ul {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.65;
  color: var(--text-primary);
}

ul b {
  font-weight: 600;
}

.replay-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 40px;
  margin-top: 4px;
  font-size: 13px;
  font-weight: 600;
  color: var(--accent-contrast);
  background: var(--accent);
  border: none;
  border-radius: 8px;
  cursor: pointer;
}
</style>
